/* eslint-disable max-len */

import { AppInstance } from '../services/app-instance.service';
import { DirectusPushNotificationRegister } from '../services/push-notification.service';
import { Organization } from './organization';

/* eslint-disable @typescript-eslint/naming-convention */
export interface UserPreferences {
  calenderOptIns: {
    reminders: boolean;
    updates: boolean;
  };
  marketingOptIns: {
    pushMessages: boolean;
  };
}

export interface Userdata {
  id?: number;
  directus_user?: string;
  language?: string;
  favorite_locations?: string[];
  favorite_content?: string[];
  favorite_announcements?: string[];
  push_notification?: DirectusPushNotificationRegister;
}

export interface LocationRequestFilters {
  categoryIds?: string[];
}

export interface OrganizationBranding {
  primaryColor: string;
  secondaryColor: string;
  tertiaryColor: string;
  greeting: string;
}

export class User {
  organizationId: string;
  organizationSlug: string;
  language: string;
  skipOnboarding: boolean;
  userdata: Userdata = {};
  profileSettings: UserPreferences = {} as UserPreferences;
  showDesktopRedirect = true;
  branding: OrganizationBranding;
  areas: string[];
  appInstance: AppInstance;
  areaId: string;
  locationRequestFilters: LocationRequestFilters = {
    categoryIds: [],
  };
  showNotificationAlertForIos = true;
  enableSeutuluotsiAnnouncements = true;

  private filterCount = 0;

  constructor() {}

  setLanguage = (language: string) => {
    this.language = language;
    if (!this.userdata) {
      this.userdata = {};
    }
    this.userdata.language = language;
    this.saveToLocalstorage();
  };

  setOnboarding = (skipOnboarding: boolean) => {
    this.skipOnboarding = skipOnboarding;
    this.saveToLocalstorage();
  };

  setProfileSettings = (preferences: UserPreferences) => {
    this.profileSettings = preferences;
    this.saveToLocalstorage();
  };

  // Favorite content based on componentType
  setFavorites = (contentId: string, componentType: string) => {
    if (!this.userdata) {
      return false;
    }

    if (!this.userdata.favorite_content) {
      this.userdata.favorite_content = [];
    }

    // Content Page
    if (componentType === 'ContentPagePage') {
      const favoriteContentsCopy = JSON.parse(JSON.stringify(this.userdata.favorite_content));
      const index = favoriteContentsCopy.indexOf(contentId);
      if (index === -1) {
        favoriteContentsCopy.push(contentId);
      } else {
        favoriteContentsCopy.splice(index, 1);
      }
      this.userdata.favorite_content = favoriteContentsCopy;
      this.saveToLocalstorage();
      return true;

      // Organizations
    } else if (componentType === 'OrganizationPage') {
      let favOrgsCopy = [];
      if (this.userdata.favorite_locations) {
        favOrgsCopy = JSON.parse(JSON.stringify(this.userdata.favorite_locations));
      }
      const indexOfOrgId = favOrgsCopy.indexOf(contentId);
      if (indexOfOrgId === -1) {
        favOrgsCopy.push(contentId);
      } else {
        favOrgsCopy.splice(indexOfOrgId, 1);
      }
      this.userdata.favorite_locations = favOrgsCopy;
      this.saveToLocalstorage();
      return true;

      // Announcements
    } else if (componentType === 'AnnouncementPage') {
      console.log(this.userdata.favorite_announcements);
      let announcementsCopy = [];
      if (this.userdata.favorite_announcements) {
        announcementsCopy = JSON.parse(JSON.stringify(this.userdata.favorite_announcements));
      }
      const indexOfAnnouncements = announcementsCopy.indexOf(contentId);
      if (indexOfAnnouncements === -1) {
        announcementsCopy.push(contentId);
      } else {
        announcementsCopy.splice(indexOfAnnouncements, 1);
      }
      this.userdata.favorite_announcements = announcementsCopy;
      this.saveToLocalstorage();
      return true;

      // No component found
    } else {
      console.log(`componentType '${componentType}' not recognized`);
      return false;
    }
  };

  setLocationRequestFilters = (locationRequestFilters: LocationRequestFilters) => {
    if (locationRequestFilters.categoryIds) {
      this.locationRequestFilters.categoryIds = locationRequestFilters.categoryIds;
    }
    this.saveToLocalstorage();
  };

  setOrganization = (organization: Organization) => {
    const organizationId = organization.id;
    const newBranding: OrganizationBranding = {
      primaryColor: organization.branding?.primary_color
        ? organization.branding.primary_color
        : '#2F3366',
      secondaryColor: organization.branding?.secondary_color
        ? organization.branding.secondary_color
        : 'white',
      greeting: organization.branding?.greeting,
      tertiaryColor: organization.branding?.tertiary_color,
    };
    this.organizationId = organizationId;
    this.branding = newBranding;
    this.organizationSlug = organization.slug;
    this.enableSeutuluotsiAnnouncements = organization.enableSeutuluotsiAnnouncements;
    this.saveToLocalstorage();
  };

  setArea = (selectedArea: string, areaHierarchy: string[]) => {
    this.areas = areaHierarchy;
    this.areaId = selectedArea;
    this.saveToLocalstorage();
  };

  setAppInstance = (settings: AppInstance) => {
    this.appInstance = settings;
    this.saveToLocalstorage();
  };

  setPushNotification = (settings: DirectusPushNotificationRegister) => {
    if (!this.userdata) {
      this.userdata = {};
    }
    if (!this.userdata.push_notification) {
      this.userdata.push_notification = {
        token: '',
        tags: [],
      };
    }
    if (settings.token) {
      this.userdata.push_notification.token = settings.token;
    }
    if (settings.tags) {
      this.userdata.push_notification.tags = settings.tags;
    }
    this.saveToLocalstorage();
    return this.userdata.push_notification;
  };

  setFilterCount = (filterCount: number) => {
    this.filterCount = filterCount;
    this.saveToLocalstorage();
  };

  getFilterCount = () => {
    if (!this.filterCount) {
      this.filterCount = 0;
    }
    return this.filterCount;
  };

  setDesktopRedirect = (showDesktopRedirect: boolean) => {
    this.showDesktopRedirect = showDesktopRedirect;
    this.saveToLocalstorage();
  };

  setShowNotificationAlertForIos = (showNotificationAlertForIos: boolean) => {
    this.showNotificationAlertForIos = showNotificationAlertForIos;
    this.saveToLocalstorage();
  };

  saveToLocalstorage = () => {
    localStorage.setItem('user', JSON.stringify(this.generateDTO()));
  };

  readFromLocalStorage = () => {
    const foundUser = JSON.parse(localStorage.getItem('user'));
    if (foundUser) {
      this.language = foundUser.language;
      this.skipOnboarding = foundUser.skipOnboarding;
      this.profileSettings = foundUser.profileSettings;
      this.userdata = foundUser.userdata ?? {};
      this.locationRequestFilters = foundUser.serviceFilters;
      this.filterCount = foundUser.filterCount;
      this.showDesktopRedirect = foundUser.showDesktopRedirect;
      this.organizationId = foundUser.organizationId;
      this.areas = foundUser.areas;
      this.areaId = foundUser.areaId;
      this.branding = foundUser.branding;
      this.organizationSlug = foundUser.organizationSlug;
      this.appInstance = foundUser.appInstance;
      this.showNotificationAlertForIos = foundUser.showNotificationAlertForIos;
      this.enableSeutuluotsiAnnouncements = foundUser.enableSeutuluotsiAnnouncements;
    }
  };

  removeFromLocalStorage = () => {
    localStorage.removeItem('user');
  };

  generateDTO = () => ({
    language: this.language,
    skipOnboarding: this.skipOnboarding,
    profileSettings: this.profileSettings,
    userdata: this.userdata,
    serviceFilters: this.locationRequestFilters,
    filterCount: this.filterCount,
    showDesktopRedirect: this.showDesktopRedirect,
    organizationId: this.organizationId,
    areas: this.areas,
    areaId: this.areaId,
    branding: this.branding,
    organizationSlug: this.organizationSlug,
    appInstance: this.appInstance,
    showNotificationAlertForIos: this.showNotificationAlertForIos,
    enableSeutuluotsiAnnouncements: this.enableSeutuluotsiAnnouncements,
  });
}
